/**
 * @file auth.js
 * @description 登录态 token 读写；配合 axios 拦截器注入 COMIC-WEB-TOKEN
 * @module shared/utils/auth
 */

import { getStoredUser, setStoredUser, clearStoredUser } from './storage'

/** 请求头 token 字段名（与后端约定一致） */
export const AUTH_TOKEN_HEADER = 'COMIC-WEB-TOKEN'

/** 与后端 Redis 30Day 桶对齐的本地 metadata TTL（鉴权仍以服务端 10474 为准） */
export const WEB_SESSION_TTL_MS = 30 * 24 * 60 * 60 * 1000

const TOKEN_STORAGE_KEY = AUTH_TOKEN_HEADER

/**
 * @param {unknown} user
 * @returns {boolean}
 */
export function isStoredSessionUsable(user) {
  if (!user || typeof user !== 'object') return false
  const token = /** @type {Record<string, unknown>} */ (user).token
  return typeof token === 'string' && token.trim().length > 0
}

/**
 * 登录/注册/OAuth 成功后构造写入 localStorage 的会话对象
 * @param {Record<string, unknown>} tokenVo 接口 data（含 token、name、avatar 等）
 * @returns {Record<string, unknown>}
 */
export function buildStoredSession(tokenVo) {
  const token =
    typeof tokenVo.token === 'string' ? tokenVo.token.trim() : ''
  return {
    ...tokenVo,
    token,
    expiration: Date.now() + WEB_SESSION_TTL_MS,
  }
}

/**
 * 将 token 同步到文档推荐的 localStorage key（与 user 对象并存）
 * @param {string} token
 */
function syncTokenStorageKey(token) {
  if (typeof localStorage === 'undefined') return
  if (token) {
    localStorage.setItem(TOKEN_STORAGE_KEY, token)
  } else {
    localStorage.removeItem(TOKEN_STORAGE_KEY)
  }
}

/**
 * 从缓存用户对象读取 token
 * @returns {string}
 */
export function getAuthToken() {
  const user = getStoredUser()
  const fromUser = user?.token
  if (typeof fromUser === 'string' && fromUser.trim()) {
    return fromUser.trim()
  }
  if (typeof localStorage !== 'undefined') {
    const legacy = localStorage.getItem(TOKEN_STORAGE_KEY)
    if (typeof legacy === 'string' && legacy.trim()) return legacy.trim()
  }
  return ''
}

/**
 * 读取可恢复的本地会话（有 token 即返回，不因 expiration 丢弃）
 * @returns {Record<string, unknown> | null}
 */
export function getRestorableSession() {
  const user = getStoredUser()
  if (isStoredSessionUsable(user)) {
    return /** @type {Record<string, unknown>} */ (user)
  }
  const token = getAuthToken()
  if (!token) return null
  return buildStoredSession({ token })
}

/**
 * 应用启动时恢复登录态到内存（不删除 storage）
 * @returns {Record<string, unknown> | null}
 */
export function restoreAuthSession() {
  const session = getRestorableSession()
  if (!session) return null
  if (!getStoredUser()?.token) {
    saveAuthUser(session)
  }
  return session
}

/**
 * 合并更新缓存用户信息（登录/注册成功后）
 * @param {Record<string, unknown>} userData
 */
export function saveAuthUser(userData) {
  setStoredUser(userData)
  const token = typeof userData.token === 'string' ? userData.token.trim() : ''
  syncTokenStorageKey(token)
}

/**
 * 登出：清除本地用户缓存
 */
export function clearAuthSession() {
  clearStoredUser()
  syncTokenStorageKey('')
}

/**
 * 生成带 token 的请求头对象
 * @returns {Record<string, string>}
 */
export function getAuthHeaders() {
  const token = getAuthToken()
  if (!token) return {}
  return { [AUTH_TOKEN_HEADER]: token }
}

/**
 * 从缓存用户读取微信 openId（JSAPI / 小程序打赏必填）
 * @returns {string}
 */
export function getWxOpenId() {
  const user = getStoredUser()
  if (!user || typeof user !== 'object') return ''
  const candidates = [user.wxOpenId, user.openId, user.wxOpenid, user.wechatOpenId]
  for (const id of candidates) {
    if (typeof id === 'string' && id.trim()) return id.trim()
  }
  return ''
}
